import React from 'react';

interface ServiceRequestDetailsModalProps {
    request: any;
    onClose: () => void;
}

const ServiceRequestDetailsModal: React.FC<ServiceRequestDetailsModalProps> = ({ request, onClose }) => {
    const getStatusBadge = (status: string) => {
        const statusConfig = {
            draft: { color: 'bg-secondary', text: 'Draft' },
            pending: { color: 'bg-warning', text: 'Pending' },
            reviewing: { color: 'bg-info', text: 'Reviewing' },
            negotiating: { color: 'bg-primary', text: 'Negotiating' },
            patient_approval: { color: 'bg-warning', text: 'Your Approval Needed' },
            agreed: { color: 'bg-success', text: 'Agreed' },
            rejected: { color: 'bg-danger', text: 'Rejected' },
            cancelled: { color: 'bg-dark', text: 'Cancelled' },
            upgraded: { color: 'bg-success', text: 'Collaboration' }
        };
        
        const config = statusConfig[status as keyof typeof statusConfig] || { color: 'bg-secondary', text: status };
        return <span className={`badge ${config.color} text-white`}>{config.text}</span>;
    };

    const getPriorityBadge = (priority: string) => {
        const priorityConfig = {
            low: { color: 'bg-success', text: 'Low' },
            normal: { color: 'bg-warning', text: 'Normal' },
            high: { color: 'bg-danger', text: 'High' }
        };
        
        const config = priorityConfig[priority as keyof typeof priorityConfig];
        return <span className={`badge ${config.color} text-white`}>{config.text}</span>;
    };

    return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-clipboard-list me-2"></i>
                            Service Request Details
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    
                    <div className="modal-body">
                        <div className="row g-3">
                            <div className="col-12">
                                <h6 className="text-primary mb-3">Request Information</h6>
                                <div className="row g-2">
                                    <div className="col-md-8">
                                        <small className="text-muted">Title</small>
                                        <div className="fw-medium">{request.title}</div>
                                    </div>
                                    <div className="col-md-4">
                                        <small className="text-muted">Status</small>
                                        <div>{getStatusBadge(request.status)}</div>
                                    </div>
                                    <div className="col-12">
                                        <small className="text-muted">Description</small>
                                        <div className="fw-medium">{request.description}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <h6 className="text-primary mb-3">Request Details</h6>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <small className="text-muted">Priority</small>
                                        <div>{getPriorityBadge(request.priority)}</div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Type</small>
                                        <div className="fw-medium text-capitalize">{request.request_type}</div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Requesting Clinic</small>
                                        <div className="fw-medium">{request.requesting_clinic}</div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Applications</small>
                                        <div className="fw-medium">{request.applications_count}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <h6 className="text-primary mb-3">Timing</h6>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <small className="text-muted">Start Date</small>
                                        <div className="fw-medium">{new Date(request.start_date).toLocaleDateString()}</div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">End Date</small>
                                        <div className="fw-medium">
                                            {request.end_date ? new Date(request.end_date).toLocaleDateString() : 'Not specified'}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Response Time</small>
                                        <div className="fw-medium">{request.response_time} hours</div>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Created</small>
                                        <div className="fw-medium">{new Date(request.created).toLocaleDateString()}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12">
                                <h6 className="text-primary mb-3">Information Sharing Settings</h6>
                                <div className="alert alert-info">
                                    <i className="fas fa-info-circle me-2"></i>
                                    <strong>Your Privacy:</strong> These are the information sharing settings for this request.
                                </div>
                                <div className="row g-2">
                                    <div className="col-md-4">
                                        <div className="d-flex align-items-center">
                                            <i className={`fas fa-${request.share_patient_demographics ? 'check text-success' : 'times text-danger'} me-2`}></i>
                                            <span>Demographics</span>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="d-flex align-items-center">
                                            <i className={`fas fa-${request.share_medical_history ? 'check text-success' : 'times text-danger'} me-2`}></i>
                                            <span>Medical History</span>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="d-flex align-items-center">
                                            <i className={`fas fa-${request.share_current_medications ? 'check text-success' : 'times text-danger'} me-2`}></i>
                                            <span>Medications</span>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="d-flex align-items-center">
                                            <i className={`fas fa-${request.share_lab_results ? 'check text-success' : 'times text-danger'} me-2`}></i>
                                            <span>Lab Results</span>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="d-flex align-items-center">
                                            <i className={`fas fa-${request.share_imaging ? 'check text-success' : 'times text-danger'} me-2`}></i>
                                            <span>Imaging</span>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="d-flex align-items-center">
                                            <i className={`fas fa-${request.share_notes ? 'check text-success' : 'times text-danger'} me-2`}></i>
                                            <span>Clinical Notes</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12">
                                <h6 className="text-primary mb-3">Your Consent Status</h6>
                                <div className="alert alert-info">
                                    <div className="d-flex align-items-center">
                                        <i className={`fas fa-${request.patient_consent_given ? 'check-circle text-success' : 'clock text-warning'} me-2`}></i>
                                        <div>
                                            <strong>
                                                {request.patient_consent_given ? 'Consent Given' : 'Consent Pending'}
                                            </strong>
                                            {request.patient_consent_date && (
                                                <div className="small text-muted">
                                                    Given on: {new Date(request.patient_consent_date).toLocaleDateString()}
                                                </div>
                                            )}
                                            {request.patient_consent_notes && (
                                                <div className="small text-muted mt-1">
                                                    Notes: {request.patient_consent_notes}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceRequestDetailsModal; 